import polygonClipping from "polygon-clipping";
import { REGION_GEO, REGION_ORDER } from "../jawselRegions";

// Geometri pulau Jawsel: proyeksi pseudo-isometrik, kamera, dan garis pantai (union wilayah).
export const NAMES = {
  kediri: "Kediri", blitar: "Blitar", nganjuk: "Nganjuk", trenggalek: "Trenggalek",
  tulungagung: "Tulungagung", madiun: "Madiun", magetan: "Magetan", pacitan: "Pacitan",
  ponorogo: "Ponorogo", ngawi: "Ngawi",
};
export const BBOX = (() => {
  let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
  for (const r of Object.values(REGION_GEO)) {
    for (const ring of [r.pts, ...(r.extra || [])]) for (const [x, y] of ring) {
      if (x < x0) x0 = x; if (x > x1) x1 = x;
      if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
  }
  return { x0, y0, x1, y1, w: x1 - x0, h: y1 - y0, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
})();
function projectRaw(x, y, angle, squash) {
  const dx = x - BBOX.cx, dy = y - BBOX.cy;
  const ca = Math.cos(angle), sa = Math.sin(angle);
  return [dx * ca - dy * sa, (dx * sa + dy * ca) * squash];
}
export function viewFor(W, H) {
  const compact = W < 640;
  const angle = compact ? -0.18 : -0.26;
  const squash = compact ? 0.74 : 0.46;
  let u0 = 1e9, v0 = 1e9, u1 = -1e9, v1 = -1e9;
  for (const r of Object.values(REGION_GEO)) {
    for (const ring of [r.pts, ...(r.extra || [])]) for (const [x, y] of ring) {
      const [u, v] = projectRaw(x, y, angle, squash);
      if (u < u0) u0 = u; if (u > u1) u1 = u;
      if (v < v0) v0 = v; if (v > v1) v1 = v;
    }
  }
  const padX = compact ? 0.05 : 0.07;
  const padTop = compact ? 0.20 : 0.07;
  const padBottom = compact ? 0.20 : 0.10;
  const availW = W * (1 - padX * 2);
  const availH = H * (1 - padTop - padBottom);
  const s = Math.min(availW / (u1 - u0), availH / (v1 - v0));
  const ox = (W - (u1 - u0) * s) / 2;
  const oy = H * padTop + (availH - (v1 - v0) * s) / 2;
  return { s, ox, oy, u0, v0, angle, squash, compact };
}
export function withCam(v, cam, W, H) {
  const z = cam.z || 1;
  return {
    ...v,
    s: v.s * z,
    ox: z * (v.ox - W / 2) + W / 2 + cam.x,
    oy: z * (v.oy - H / 2) + H / 2 + cam.y,
  };
}

export function mapPoint(v, x, y, bob = 0) {
  const [u, w] = projectRaw(x, y, v.angle, v.squash);
  return [v.ox + (u - v.u0) * v.s, v.oy + (w - v.v0) * v.s + bob];
}
export function pathD(pts, v) {
  return pts.map((p, i) => {
    const [x, y] = mapPoint(v, p[0], p[1], 0);
    return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ") + " Z";
}
export function inPoly(x, y, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
    const hit = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-9) + xi);
    if (hit) inside = !inside;
  }
  return inside;
}

// ===== TEBING PULAU: EKSTRUSI GEOMETRIS =====
// Garis pantai = UNION semua poligon wilayah (ruang peta), dihitung sekali.
// Tiap tepi pantai yang menghadap penonton ditarik lurus ke bawah sedalam DEPTH,
// sehingga dinding tebing menempel PERSIS pada tepi hijau (tanpa celah/menggantung).
export const COAST_RINGS = (() => {
  const geoms = [];
  for (const id of REGION_ORDER) {
    const r = REGION_GEO[id];
    for (const ring of [r.pts, ...(r.extra || [])]) geoms.push([ring]);
  }
  return polygonClipping.union(...geoms).map(poly => {
    const ring = poly[0]; // ring terluar; lubang sliver antar batas diabaikan
    const [fx, fy] = ring[0], [lx, ly] = ring[ring.length - 1];
    return fx === lx && fy === ly ? ring.slice(0, -1) : ring;
  });
})();
